/**
 * PushPermissionPrompt Component
 *
 * Non-intrusive, post-login soft prompt for enabling browser push.
 * Shown only to an authenticated user whose permission is still 'default'
 * and who hasn't dismissed it (7-day) or opted out ('never'). On Enable it
 * requests permission, fetches the VAPID key from the server, and subscribes
 * via the single push-subscription utility path (utils/push-subscription).
 *
 * @module components/notifications/PushPermissionPrompt
 */

import { useState, useEffect, useCallback } from 'react';
import { Bell, X } from 'lucide-react';
import { api } from '../../services/api';
import { useToast } from '../../context/ToastContext';
import { useAuth } from '../../context/AuthContext';
import {
  isPushSupported,
  getPermissionStatus,
  requestNotificationPermission,
  subscribeToPush,
} from '../../utils/push-subscription';
import { isStandalone, isIos } from '../../utils/pwa';

/** Local storage keys for tracking prompt dismissal. */
const DISMISSED_KEY = 'schoolsaas_push_prompt_dismissed';
const DISMISSED_UNTIL_KEY = 'schoolsaas_push_prompt_dismissed_until';

/** How long "Not now" (and an unanswered permission dialog) quiets the banner for. */
const SNOOZE_MS = 7 * 24 * 60 * 60 * 1000;

/**
 * Roles that ARE a push audience. APP_OWNER is deliberately excluded (NP-4): the
 * owner isn't push-subscribable (an owner token 401s the school-scoped push routes,
 * which previously logged Baba out on Enable), so the banner must never render or
 * act in an owner session — in both browser-tab and installed contexts.
 */
const SCHOOL_ROLES: ReadonlyArray<string> = ['SUPER_ADMIN', 'ADMIN', 'EMPLOYEE', 'STUDENT'];

/**
 * PushPermissionPrompt shows a non-intrusive banner asking an authed user to
 * enable push. Subscribes through the single utils/push-subscription path
 * (VAPID key fetched from GET /notifications/push/vapid-key at click time).
 *
 * @returns Push permission prompt element or null.
 */
export function PushPermissionPrompt(): JSX.Element | null {
  const { user } = useAuth();
  const { showToast } = useToast();
  const [isVisible, setIsVisible] = useState(false);
  const [isRequesting, setIsRequesting] = useState(false);
  /**
   * What the banner is currently saying. ONBOARD-FIX-1 A2.3 — the old code had no mode at
   * all: every non-'granted' outcome produced one "permission was denied" toast and hid
   * the banner, so a user who pressed Allow was told they had blocked us.
   *  - 'ask'    — the normal soft prompt.
   *  - 'denied' — the browser really is blocking us; it will not re-prompt, so we show a
   *               persistent recovery hint instead of a toast that scrolls away (NP-3a).
   *  - 'error'  — permission is fine but the subscribe pipeline failed. Retryable, and
   *               explicitly NOT worded as a denial.
   */
  const [mode, setMode] = useState<'ask' | 'denied' | 'error'>('ask');
  const [errorDetail, setErrorDetail] = useState<string>('');

  /**
   * Silent reconciliation: permission is already 'granted' but the SERVER may have no
   * subscription for this device (e.g. a prior attempt that subscribed the browser and
   * then died on the POST). Subscribe quietly — no banner, no toast, never a logout.
   *
   * ONBOARD-FIX-1 A2.3: this used to `return` early when the browser already held a
   * subscription, which is exactly the stranded state — the browser looked subscribed
   * forever while the server had never heard of it, so push stayed permanently dead.
   * subscribeToPush re-sends an existing subscription, so call it unconditionally.
   */
  const reconcileSubscription = useCallback(async (): Promise<void> => {
    try {
      if (!isPushSupported()) return;
      const { data } = await api.get('/notifications/push/vapid-key');
      const publicKey = data?.data?.publicKey as string | undefined;
      if (publicKey) await subscribeToPush(publicKey);
    } catch {
      /* best-effort — a failed reconcile must never surface or log the user out */
    }
  }, []);

  /** Decide whether to show: school-role session, permission still default, not dismissed. */
  useEffect(() => {
    // NP-4: school roles only — APP_OWNER (owner shell) is never a push audience.
    if (!user || !SCHOOL_ROLES.includes(user.role)) return;
    if (!isPushSupported()) return;
    // IB-1: on iOS, push ONLY works in the installed PWA (standalone) — asking in
    // Safari is a dead-end. So on iOS, defer the push ask until AFTER install
    // (standalone); the PWAInstallPrompt handles "Add to Home Screen" in Safari first.
    if (isIos() && !isStandalone()) return;

    const perm = getPermissionStatus();
    if (perm === 'unsupported') return; // nothing to ask for
    if (perm === 'denied') return; // blocked — the browser won't re-prompt anyway
    if (perm === 'granted') { void reconcileSubscription(); return; } // ensure subscribed, no banner

    /**
     * Async re-entry (ONBOARD-FIX-1 A2.3). Chrome's quiet permission UI resolves
     * requestPermission() with 'default' and lets the user allow later from the
     * address-bar bell; nothing used to notice, so the grant never became a
     * subscription until a full page reload. Watch for the change and finish the job.
     *
     * Registered HERE, inside the effect, deliberately: it sits after the NP-4 role gate,
     * the support gate and the iOS-Safari gate above. A global listener would re-open both
     * the owner-logout bug (NP-4) and the iOS dead-end that IB-1 closed.
     */
    let permStatus: PermissionStatus | null = null;
    const onPermissionChange = (): void => {
      if (permStatus?.state === 'granted') {
        setIsVisible(false);
        void reconcileSubscription();
      }
    };
    try {
      void navigator.permissions
        ?.query({ name: 'notifications' as PermissionName })
        .then((status) => {
          permStatus = status;
          status.addEventListener('change', onPermissionChange);
        })
        .catch(() => {
          /* Safari/WebKit may reject this descriptor — degrade silently */
        });
    } catch {
      /* older WebKit throws synchronously on the descriptor */
    }

    // perm === 'default' → soft prompt, honouring the dismissal windows.
    const dismissedUntil = localStorage.getItem(DISMISSED_UNTIL_KEY);
    if (dismissedUntil) {
      const until = parseInt(dismissedUntil, 10);
      if (Date.now() < until) return;
      localStorage.removeItem(DISMISSED_KEY);
      localStorage.removeItem(DISMISSED_UNTIL_KEY);
    }

    // Permanent opt-out
    if (localStorage.getItem(DISMISSED_KEY) === 'permanent') return;

    // Show after a short delay (don't pounce on cold open)
    const timer = setTimeout(() => setIsVisible(true), 3000);
    return () => {
      clearTimeout(timer);
      permStatus?.removeEventListener('change', onPermissionChange);
    };
  }, [user, reconcileSubscription]);

  /**
   * Request permission, fetch the VAPID key, subscribe.
   *
   * ONBOARD-FIX-1 A2.3 — this handler is where the reported bug lived. It read
   * `if (permission !== 'granted')` and showed "Notification permission was denied.",
   * so a dismissal, Chrome's quiet UI and legacy Safari's `undefined` were all reported
   * to the user as a block — and `subscribeToPush` was never even reached. The four
   * outcomes are now genuinely distinct:
   *   granted   → subscribe → success, or a RETRYABLE error that never says "denied"
   *   denied    → persistent recovery panel (the browser will not ask again)
   *   default   → quiet 7-day snooze; a dismissal is not an error and gets no red toast
   *   unsupported → say so plainly, once
   */
  const handleEnable = useCallback(async (): Promise<void> => {
    if (isRequesting) return;
    setIsRequesting(true);
    try {
      // Must be first — Firefox/Safari require transient user activation, so nothing
      // may be awaited before this call.
      const permission = await requestNotificationPermission();

      if (permission === 'unsupported') {
        showToast('info', 'This browser does not support notifications.');
        setIsVisible(false);
        return;
      }

      if (permission === 'denied') {
        // Not a toast: the browser will not re-prompt, so the user needs standing
        // instructions they can act on, not a message that disappears (NP-3a).
        setMode('denied');
        return;
      }

      if (permission === 'default') {
        // Dismissed / "Not now" / quiet-UI. A non-answer is not a failure (NP-3b) —
        // snooze exactly like the Not-now button, silently. (The old code hid the
        // banner without writing any key, so it nagged again on the next load.)
        localStorage.setItem(DISMISSED_UNTIL_KEY, (Date.now() + SNOOZE_MS).toString());
        setIsVisible(false);
        return;
      }

      const { data } = await api.get('/notifications/push/vapid-key');
      const publicKey = data?.data?.publicKey as string | undefined;
      if (!publicKey) {
        setErrorDetail('Notifications are not configured on the server yet.');
        setMode('error');
        return;
      }

      const result = await subscribeToPush(publicKey);
      if (result.ok) {
        showToast('success', 'Notifications enabled!');
        setIsVisible(false);
        return;
      }

      // Permission IS granted — this is a setup failure, and saying "denied" here is
      // exactly the lie we are fixing.
      setErrorDetail(
        result.reason === 'sw'
          ? 'The app is still starting up on this device. Try again in a moment.'
          : result.reason === 'server'
            ? 'We could not reach the server to finish setting this up.'
            : result.reason === 'unsupported'
              ? 'This browser does not support push notifications.'
              : 'Something went wrong while setting up notifications.',
      );
      setMode('error');
    } catch (error) {
      console.error('Push subscription failed:', error);
      setErrorDetail('Something went wrong while setting up notifications.');
      setMode('error');
    } finally {
      setIsRequesting(false);
    }
  }, [isRequesting, showToast]);

  /** Dismiss for 7 days. */
  const handleDismiss = useCallback((): void => {
    localStorage.setItem(DISMISSED_UNTIL_KEY, (Date.now() + SNOOZE_MS).toString());
    setIsVisible(false);
  }, []);

  /** Permanent opt-out. */
  const handleNeverShow = useCallback((): void => {
    localStorage.setItem(DISMISSED_KEY, 'permanent');
    setIsVisible(false);
  }, []);

  if (!isVisible) return null;

  return (
    <div
      data-testid="push-permission-prompt"
      role="dialog"
      aria-label="Enable notifications"
      style={{
        position: 'fixed',
        left: '50%',
        transform: 'translateX(-50%)',
        bottom: 'calc(env(safe-area-inset-bottom, 0px) + 16px)',
        zIndex: 99998,
        width: 'min(calc(100vw - 32px), 420px)',
        fontFamily: 'var(--font-body)',
      }}
    >
      <div
        style={{
          background: 'var(--surface)',
          border: '1.5px solid var(--border)',
          borderRadius: 18,
          boxShadow: '0 12px 44px rgba(13, 11, 30, 0.20)',
          padding: 16,
          animation: 'push-prompt-rise 0.28s ease-out',
        }}
      >
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
          <div
            style={{
              width: 40,
              height: 40,
              borderRadius: 12,
              flexShrink: 0,
              background: 'linear-gradient(135deg, var(--p), #8B5CF6)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 4px 14px rgba(124, 58, 237, 0.35)',
            }}
          >
            <Bell size={20} color="#fff" strokeWidth={2.2} />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <h3 style={{ margin: 0, fontSize: 15, fontWeight: 600, color: 'var(--t1)', letterSpacing: '-0.01em' }}>
              {mode === 'denied'
                ? 'Notifications are blocked'
                : mode === 'error'
                  ? "Almost there — we couldn't finish"
                  : 'Enable notifications'}
            </h3>
            <p style={{ margin: '3px 0 0', fontSize: 13, lineHeight: 1.45, color: 'var(--t3)' }}>
              {mode === 'denied'
                ? 'Your browser is blocking notifications for this site, so we cannot ask again from here. Tap the lock or ⓘ icon next to the address bar → Notifications → Allow. On Android Chrome: ⋮ → Site settings → Notifications.'
                : mode === 'error'
                  ? errorDetail
                  : 'Get instant updates about attendance, fees, exams, and important announcements.'}
            </p>
          </div>
          <button
            type="button"
            onClick={handleDismiss}
            aria-label="Dismiss"
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              color: 'var(--t3)',
              padding: 2,
              margin: '-2px -2px 0 0',
              lineHeight: 0,
              borderRadius: 8,
              flexShrink: 0,
            }}
          >
            <X size={18} />
          </button>
        </div>

        {/* Actions */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 14 }}>
          {/* A real browser block cannot be re-prompted from a click, so there is no
              Enable button in 'denied' mode — offering one that silently does nothing
              is what NP-3(a) called a dead end. */}
          {mode !== 'denied' && (
            <button
              type="button"
              onClick={handleEnable}
              disabled={isRequesting}
              style={{
                flex: 1,
                height: 40,
                borderRadius: 11,
                border: 'none',
                background: 'linear-gradient(135deg, var(--p), #8B5CF6)',
                color: '#fff',
                fontSize: 13.5,
                fontWeight: 700,
                cursor: isRequesting ? 'default' : 'pointer',
                opacity: isRequesting ? 0.6 : 1,
                fontFamily: 'var(--font-body)',
              }}
            >
              {isRequesting ? 'Enabling…' : mode === 'error' ? 'Try again' : 'Enable'}
            </button>
          )}
          <button
            type="button"
            onClick={handleDismiss}
            style={{
              height: 40,
              padding: '0 14px',
              borderRadius: 11,
              background: mode === 'denied' ? 'linear-gradient(135deg, var(--p), #8B5CF6)' : 'var(--surface-2)',
              border: mode === 'denied' ? 'none' : '1px solid var(--border)',
              color: mode === 'denied' ? '#fff' : 'var(--t2)',
              fontSize: 13,
              fontWeight: mode === 'denied' ? 700 : 600,
              cursor: 'pointer',
              flex: mode === 'denied' ? 1 : undefined,
              fontFamily: 'var(--font-body)',
            }}
          >
            {mode === 'denied' ? 'Got it' : 'Not now'}
          </button>
          {mode === 'ask' && (
            <button
              type="button"
              onClick={handleNeverShow}
              style={{
                height: 40,
                padding: '0 10px',
                borderRadius: 11,
                background: 'none',
                border: 'none',
                color: 'var(--t3)',
                fontSize: 12.5,
                fontWeight: 600,
                cursor: 'pointer',
                fontFamily: 'var(--font-body)',
              }}
            >
              Never
            </button>
          )}
        </div>
      </div>

      <style>{`
        @keyframes push-prompt-rise {
          from { opacity: 0; transform: translateY(16px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
}

export default PushPermissionPrompt;
